/**
 * QuotaProgressBar Component
 *
 * Enhanced progress bar for quota visualization with color coding.
 * Supports different sizes and alert levels.
 */

import React from 'react';
import { clsx } from 'clsx';

export interface QuotaProgressBarProps {
  used: number;
  limit: number | null;
  size?: 'sm' | 'md' | 'lg';
  showLabel?: boolean;
  showPercentage?: boolean;
  showResetTime?: boolean;
  resetAt?: string | null;
  warningThreshold?: number;
  criticalThreshold?: number;
  className?: string;
}

const sizeClasses = {
  sm: 'h-2',
  md: 'h-3',
  lg: 'h-4',
};

const formatNumber = (num: number): string => {
  if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
  if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
  return num.toString();
};

const formatResetTime = (resetAt: string | null): string => {
  if (!resetAt) return '';
  try {
    const date = new Date(resetAt);
    const now = new Date();
    const diffMs = date.getTime() - now.getTime();
    if (diffMs <= 0) return '已重置';
    const diffMins = Math.floor(diffMs / 60000);
    if (diffMins < 60) return `${diffMins}分钟后重置`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}小时后重置`;
    return `${Math.floor(diffHours / 24)}天后重置`;
  } catch {
    return '';
  }
};

export const QuotaProgressBar: React.FC<QuotaProgressBarProps> = ({
  used,
  limit,
  size = 'md',
  showLabel = true,
  showPercentage = true,
  showResetTime = false,
  resetAt,
  warningThreshold = 70,
  criticalThreshold = 90,
  className = '',
}) => {
  if (limit === null) {
    return (
      <div className={clsx('space-y-1', className)}>
        {showLabel && (
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-600">使用量</span>
            <span className="font-medium text-gray-800">
              {formatNumber(used)}
              <span className="text-gray-500 ml-1">(无限制)</span>
            </span>
          </div>
        )}
        {!showLabel && (
          <div className="text-xs text-gray-500">无限制</div>
        )}
      </div>
    );
  }

  const percentage = limit > 0 ? Math.min((used / limit) * 100, 100) : 0;
  const remaining = Math.max(limit - used, 0);

  let barColor = 'bg-green-500';
  let textColor = 'text-green-600';

  if (percentage >= criticalThreshold) {
    barColor = 'bg-red-500';
    textColor = 'text-red-600';
  } else if (percentage >= warningThreshold) {
    barColor = 'bg-yellow-500';
    textColor = 'text-yellow-600';
  }

  return (
    <div className={clsx('space-y-1', className)}>
      {showLabel && (
        <div className="flex items-center justify-between text-sm">
          <span className="text-gray-600">使用量</span>
          <div className="flex items-center gap-2">
            <span className={clsx('font-medium', textColor)}>
              {formatNumber(used)} / {formatNumber(limit)}
            </span>
            {showPercentage && (
              <span className={clsx('text-xs', textColor)}>
                ({percentage.toFixed(1)}%)
              </span>
            )}
          </div>
        </div>
      )}

      <div className={clsx('w-full bg-gray-200 rounded-full overflow-hidden relative', sizeClasses[size])}>
        <div
          className={clsx(barColor, 'rounded-full transition-all duration-150 h-full')}
          style={{ width: `${percentage}%` }}
        />
        
        {percentage >= criticalThreshold && (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="animate-pulse bg-red-200 opacity-30 rounded-full h-full w-full" />
          </div>
        )}
      </div>

      <div className="flex items-center justify-between text-xs text-gray-500">
        <span>
          剩余 <span className={clsx('font-medium', textColor)}>{formatNumber(remaining)}</span>
        </span>
        {showResetTime && resetAt && (
          <span>{formatResetTime(resetAt)}</span>
        )}
      </div>

      {!showLabel && showPercentage && (
        <div className="text-xs text-center">
          <span className={clsx('font-medium', textColor)}>
            {percentage.toFixed(1)}%
          </span>
        </div>
      )}
    </div>
  );
};

export default QuotaProgressBar;